/**
 * Produktadgang (spec §4.6): access_mode 'ALL' → null (alt synligt);
 * 'SELECTED' → union af firma- og pakkeregler (PRODUCT + COLLECTION opløst
 * til produkt-id'er via kollektionerne).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase/client";
import { useActivePriceData } from "./usePriceData";
import { useCollections } from "./useCatalog";
import type { ShopifyProduct } from "../types";

interface RawAccessRule {
  access_type: string;
  shopify_product_id: string | null;
  shopify_collection_id: string | null;
}

function usePackageAccessRules(companyId: string | null) {
  const packageIdsQuery = useQuery({
    queryKey: ["firmapakker", companyId],
    queryFn: async () => {
      // Kun tildelte OG aktive pakker (samme queryKey/logik som usePriceData).
      const { data, error } = await supabase
        .from("company_price_packages")
        .select("package_id, price_packages!inner(is_active)")
        .eq("company_id", companyId as string)
        .eq("price_packages.is_active", true);
      if (error) throw error;
      return (data ?? []).map((r) => r.package_id);
    },
    enabled: !!companyId,
  });
  const packageIds = packageIdsQuery.data;
  return useQuery({
    queryKey: ["pakkeadgang", companyId, packageIds],
    queryFn: async (): Promise<RawAccessRule[]> => {
      if (!packageIds || packageIds.length === 0) return [];
      const { data, error } = await supabase
        .from("package_product_access")
        .select("access_type, shopify_product_id, shopify_collection_id")
        .in("package_id", packageIds);
      if (error) throw error;
      return data ?? [];
    },
    enabled: packageIds != null,
  });
}

/**
 * null = alle produkter tilladt; ellers Set af tilladte produkt-id'er.
 */
export function useAllowedProductIds(): Set<string> | null {
  const priceData = useActivePriceData();
  const { data: packageRules } = usePackageAccessRules(priceData.companyId);
  const { data: collections } = useCollections();

  return useMemo(() => {
    if (priceData.accessMode === "ALL") return null;

    const allowed = new Set<string>();
    const addRule = (rule: {
      accessType?: string;
      access_type?: string;
      shopifyProductId?: string | null;
      shopify_product_id?: string | null;
      shopifyCollectionId?: string | null;
      shopify_collection_id?: string | null;
    }) => {
      const type = rule.accessType ?? rule.access_type;
      const productId = rule.shopifyProductId ?? rule.shopify_product_id;
      const collectionId = rule.shopifyCollectionId ?? rule.shopify_collection_id;
      if (type === "PRODUCT" && productId) {
        allowed.add(productId);
      } else if (type === "COLLECTION" && collectionId) {
        const collection = collections?.find((c) => c.id === collectionId);
        collection?.productIds.forEach((id) => allowed.add(id));
      }
    };

    priceData.accessRules.forEach(addRule);
    (packageRules ?? []).forEach(addRule);
    return allowed;
  }, [priceData.accessMode, priceData.accessRules, packageRules, collections]);
}

/** Filtrér en produktliste efter adgangsreglerne. */
export function filterAllowedProducts(
  products: readonly ShopifyProduct[],
  allowedIds: Set<string> | null,
): ShopifyProduct[] {
  if (allowedIds === null) return [...products];
  return products.filter((p) => allowedIds.has(p.id));
}
